/**
 * Notification Templates Mapping for Client-Side Rendering
 * Maps EventTypeCode to a render function that generates:
 *   { title, bodyParts, icon, color }
 *
 * bodyParts is an array of:
 *   { text: string }              — plain text segment
 *   { text: string, href: string } — clickable inline link
 */

import CalendarIcon from "assets/nav-icons/calendar.svg?react";
import {
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  ArrowPathRoundedSquareIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  DocumentPlusIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  DocumentArrowUpIcon,
  UserIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  CurrencyDollarIcon,
  UsersIcon,
  BellIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  CalendarIcon as CalendarIconHero,
  WrenchIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { FaWhatsapp } from "react-icons/fa";

const formatExpiryDate = (dateStr) => {
  if (!dateStr) return "Unknown Date";

  const date = new Date(dateStr);

  if (!isNaN(date)) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  // Fallback for yyyy-mm-dd
  const dateOnly = String(dateStr).split(/[ T]/)[0];

  if (dateOnly.includes("-")) {
    const [year, month, day] = dateOnly.split("-");
    return `${day}/${month}/${year}`;
  }

  return dateOnly;
};

// const formatExpiryDate = (dateStr) => {
//   if (!dateStr) return "Unknown Date";

//   const date = new Date(dateStr);
//   if (isNaN(date)) return "Unknown Date";

//   const day = String(date.getDate()).padStart(2, "0");
//   const month = String(date.getMonth() + 1).padStart(2, "0");
//   const year = date.getFullYear();

//   return `${day}/${month}/${year}`;
// };

export const NotificationTemplates = {
  // ── Queries (1100s) ──────────────────────────────────────────────────────

  1101: (payload) => ({
    // Create_Query
    title: "New Query Raised",
    bodyParts: [
      { text: "A New Query has been Raised by " },
      { text: payload.LeadName || "Unknown" },
      { type: "break" },
      { text: "Description:" },
      { text: payload.QuerySubject || "Unknown" },
    ],
    icon: ChatBubbleLeftRightIcon,
    color: "simple",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/tickets?queryId=${payload.QueryId}`
      : null,
  }),

  1103: (payload) => ({
    // Update_Query_Status
    title: "Query Status Updated",
    bodyParts: [
      { text: "Your Query Status has been Updated: " },
      { text: payload.QuerySubject || "Unknown" },
    ],
    icon: ChatBubbleLeftRightIcon,
    color: "info",
  }),

  1104: (payload) => ({
    // Query_Response_User
    title: "Lead Responded on Query",
    bodyParts: [
      { text: "Lead " },
      { text: payload.LeadName || "Unknown" },
      {
        text: ` has been responded on query on date ${payload.LastQueryUpdatedDate || "Unknown"}`,
      },
      { type: "break" },
      {
        text: `Description: ${payload.QuerySubject || payload.Description || "None"}`,
      },
      { type: "break" },
      { text: `Status: ${payload.QueryStatus || payload.Status || "Open"}` },
    ],
    icon: ChatBubbleLeftRightIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/tickets?queryId=${payload.QueryId}`
      : null,
  }),

  1105: (payload) => ({
    // Query_Response_Lead
    title: "Query Responded",
    bodyParts: [
      { text: "Your Query " },
      { text: payload.QuerySubject || "Unknown" },
      { text: " has been responded" },
    ],
    icon: ChatBubbleLeftRightIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/tickets?queryId=${payload.QueryId}`
      : null,
  }),

  1106: (payload) => ({
    // Reassign_Query
    title: "Query Reassigned",
    bodyParts: [
      { text: "Query raised by " },
      { text: payload.LeadName || "Unknown" },
      {
        text: ` has reassigned to you by ${payload.querylastUpdatedByName || "Staff Member"}`,
      },
      { type: "break" },
      {
        text: `Description: ${payload.QuerySubject || payload.Description || "None"}`,
      },
      { type: "break" },
      { text: `Status: ${payload.QueryStatus || payload.Status || "Open"}` },
    ],
    icon: ChatBubbleLeftRightIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/tickets?queryId=${payload.QueryId}`
      : null,
  }),

  // ── FollowUps (1500s) ─────────────────────────────────────────────────────

  1501: (payload) => ({
    // Create_FollowUp
    title: "Activity Created",
    bodyParts: [
      { text: "An Activity Has been scheduled with " },
      { text: payload.LeadName || "Unknown" },
      {
        text: ` on ${payload.FollowUpScheduledDate || ""} at ${payload.FollowUpScheduledTime || ""}`,
      },
      // { type: "break" },
      // { text: `Description: ${payload.FollowUpPriority || ""}` },
      // { type: "break" },
      // { text: `Note: ${payload.FollowUpNote || ""}` },
    ],
    icon: CalendarIcon,
    color: "success",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/follow-up-calendar?date=${payload.FollowUpScheduledDate || ""}`
      : null,
  }),

  1502: (payload) => ({
    // Update_FollowUp
    title: "Activity Updated",
    bodyParts: [
      { text: "Your Activity with " },
      { text: payload.LeadName || "Unknown" },
      { text: " has been updated" },
    ],
    icon: CalendarIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/follow-up-calendar?date=${payload.FollowUpScheduledDate || ""}`
      : null,
  }),

  1503: (payload) => ({
    // FollowUp_Reassigned
    title: "Activity Reassigned",
    bodyParts: [
      { text: "A Activity With " },
      { text: payload.LeadName || "Unknown" },
      { text: " has been assigned to you" },
    ],
    icon: CalendarIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/follow-up-calendar?date=${payload.FollowUpScheduledDate || ""}`
      : null,
  }),

  1504: (payload) => ({
    // Reminder_FollowUp
    title: "Activity Reminder",
    bodyParts: [
      { text: "You have a Activity scheduled with " },
      { text: payload.LeadName || "Unknown" },
      {
        text: ` on ${payload.FollowUpScheduledDate || ""} at ${payload.FollowUpScheduledTime || ""}`,
      },

    ],
    icon: CalendarIcon,
    color: "warning",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/follow-up-calendar?date=${payload.FollowUpScheduledDate || ""}`
      : null,
  }),

  1505: (payload) => {
    const formattedDateForURL = payload.FollowUpScheduledDate ? new Date(payload.FollowUpScheduledDate).toLocaleDateString('en-US') : "";
    return {
      // Create_CustomActivity
      title: "Activity Created",
      bodyParts: [
        { text: "An Activity " },
        { text: payload.FollowUpActivityName || "Unnamed" },
        {
          text: ` has been scheduled on ${formatExpiryDate(payload.FollowUpScheduledDate) || ""} at ${payload.FollowUpScheduledTime || ""}`,
        },
      ],
      icon: CalendarIcon,
      color: "success",
      action:
        payload.FollowUpIsJobActivity === "False" || payload.FollowUpIsJobActivity === false
          ? `/leads/activity-calendar?date=${formattedDateForURL}&view=month`
          : `/jobs/jobs-calendar?date=${formattedDateForURL}&view=month`,
    };
  },

  1506: (payload) => ({
    // Update_CustomActivity
    title: "Activity Updated",
    bodyParts: [
      { text: "Your Activity " },
      { text: payload.FollowUpActivityName || "Unnamed" },
      { text: " has been updated" },
      ...(payload.NotificationCreatedBy &&
        payload.LoggedinUserFullName !== payload.NotificationCreatedBy
        ? [{ text: ` by ${payload.NotificationCreatedBy}` }]
        : []),
    ],
    icon: CalendarIcon,
    color: "info",
    action:
      payload.FollowUpIsJobActivity === "False" || payload.FollowUpIsJobActivity === false
        ? `/leads/activity-calendar?date=${payload.FollowUpScheduledDate || ""}`
        : `/jobs/jobs-calendar?date=${payload.FollowUpScheduledDate || ""}`,
  }),

  1507: (payload) => ({
    // Reminder_CustomActivity
    title: "Activity Reminder",
    bodyParts: [
      { text: "You have an Activity " },
      { text: payload.FollowUpActivityName || "Unnamed" },
      {
        text: ` scheduled on ${formatExpiryDate(payload.FollowUpScheduledDate) || ""} at ${payload.FollowUpScheduledTime || ""}`,
      },

    ],
    icon: CalendarIcon,
    color: "warning",
    action:
      payload.FollowUpIsJobActivity === "False" || payload.FollowUpIsJobActivity === false
        ? `/leads/activity-calendar?date=${payload.FollowUpScheduledDate || ""}`
        : `/jobs/jobs-calendar?date=${payload.FollowUpScheduledDate || ""}`,
  }),

  // ── Leads (1400s) ─────────────────────────────────────────────────────────

  1401: (payload) => ({
    // Generate_Lead
    title: "New Lead",
    bodyParts: [
      { text: "A new lead " },
      { text: payload.LeadName || "Unknown" },
      {
        text: ` Has Been Generated By ${payload.LeadsourceFormTitle
          ? ` Form: ${payload.LeadsourceFormTitle}`
          : payload.LeadSourceName
          } and Assigned To You`,
      },
    ],
    icon: UserPlusIcon,
    color: "info",
    action: payload.LeadId ? `/leads/opportunities/${payload.LeadId}` : null,
  }),

  1405: (payload) => ({
    // Lead_Reassigned
    title: "Lead Reassigned",
    bodyParts: [
      { text: "A lead " },
      { text: payload.LeadName || "Unknown" },
      { text: " Has been Reassigned to you by " },
      { text: payload.NotificationCreatedBy || "Unknown" },
    ],
    icon: ArrowPathRoundedSquareIcon,
    color: "warning",
    action: payload.LeadId ? `/leads/opportunities/${payload.LeadId}` : null,
  }),

  1403: (payload) => ({
    // Change_Lead_Priority
    title: "Lead Priority Updated",
    bodyParts: [
      { text: "Priority of lead " },
      { text: payload.LeadName || "Unknown" },
      { text: ` has been changed to ${payload.PriorityName || "Unknown"}` },
    ],
    icon: StarIcon,
    color: "info",
    action: payload.LeadId ? `/leads/opportunities/${payload.LeadId}` : null,
  }),

  1404: (payload) => ({
    // Change_Interaction_Level
    title: "Interaction Level Updated",
    bodyParts: [
      { text: "Interaction Level of the lead " },
      { text: payload.LeadName || "Unknown" },
      {
        text: ` has been updated to ${payload.LeadInteractionLevel || "Unknown"}`,
      },
    ],
    icon: ArrowTrendingUpIcon,
    color: "info",
    action: payload.LeadId ? `/leads/opportunities/${payload.LeadId}` : null,
  }),

  1409: (payload) => ({
    // Lead_Note_Added
    title: "New Lead Note",
    bodyParts: [
      { text: "A new lead note has been added for lead " },
      { text: payload.LeadName || "Unknown" },
      { text: ` on ${payload.LeadNoteCreatedAt || ""}` },
    ],
    icon: DocumentPlusIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/notes?noteId=${payload.LeadNoteId || payload.LeadNoteId || ""}`
      : null,
  }),

  1410: (payload) => ({
    // Lead_Note_Set_Important
    title: "Lead Note",
    bodyParts: [
      { text: "A lead note for lead " },
      { text: payload.LeadName || "Unknown" },
      { text: " has been set as Important" },
    ],
    icon: ExclamationCircleIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/notes?noteId=${payload.NoteId || payload.Id || ""}`
      : null,
  }),

  1411: (payload) => ({
    // Lead_Note_Updated
    title: "Lead Note Updated",
    bodyParts: [
      { text: "A lead note for lead " },
      { text: payload.LeadName || "Unknown" },
      { text: " has been Updated by " },
      { text: payload.LeadNoteUpdatedby || "Unknown" },
      { text: ` on ${payload.LeadNoteUpdatedDate || ""}` },
    ],
    icon: PencilSquareIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/notes?noteId=${payload.NoteId || payload.Id || ""}`
      : null,
  }),

  1412: (payload) => ({
    // Lead_Document_Uploaded
    title: "New Document",
    bodyParts: [
      { text: "a new Document " },
      { text: payload.LeadDocumentName || "Unknown" },
      { text: " has been uploaded by " },
      { text: payload.DocumentUploadedby || "Unknown" },
      { text: ` on ${formatExpiryDate(payload.DocumentUploadedOn) || ""}` },
    ],
    icon: DocumentArrowUpIcon,
    color: "pink",
    action: payload.LeadId
      ? `/leads/opportunities?leadId=${payload.LeadId}&tab=documents&documentId=${payload.leadDocumentId || payload.Id || ""}`
      : null,
  }),

  1415: (payload) => ({
    // Assign_Counseller_For_Verify_Lead_Document
    title: "New Document Verifier",
    bodyParts: [
      { text: "A new Document Verifier " },
      { text: payload.LeadDocumentVerifierCounsellerName || "Unknown" },
      { text: " has been assigned for lead " },
      { text: payload.LeadName || "Unknown" },
    ],
    icon: UserIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/documents?documentId=${payload.leadDocumentId || payload.Id || ""}`
      : null,
  }),

  1414: (payload) => ({
    // Assign_Team_For_Verify_Lead_Document
    title: "New Document Verifier Team",
    bodyParts: [
      { text: "A new Document Verifier Team " },
      { text: payload.LeadDocumentVerifierTeamName || "Unknown" },
      { text: " has been assigned for lead " },
      { text: payload.LeadName || "Unknown" },
    ],
    icon: UserGroupIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/documents?documentId=${payload.leadDocumentId || payload.Id || ""}`
      : null,
  }),

  1416: (payload) => ({
    // Verify_Lead_Document
    title: "Document Verified",
    bodyParts: [
      { text: "Document " },
      { text: payload.LeadDocumentName || "Unknown" },
      { text: " Has been verified by " },
      { text: payload.DocumentVerifiedBy || "Unknown" },
      { text: ` on ${payload.DocumentVerifiedAt || ""}` },
    ],
    icon: CheckBadgeIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/documents?documentId=${payload.leadDocumentId || payload.Id || ""}`
      : null,
  }),

  1406: (payload) => ({
    // Lead_PaymentLink_Generated
    title: "New Payment Link",
    bodyParts: [
      { text: "A Payment Link has been generated for lead " },
      { text: payload.LeadName || "Unknown" },
      ...(payload.PaymentDescription
        ? [{ text: `. ${payload.PaymentDescription}` }]
        : []),
      ...(payload.paymentlinkURL ? [{ text: " — " }, { text: "Pay Now" }] : []),
    ],
    icon: CurrencyDollarIcon,
    color: "info",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/payments?paymentLinkId=${payload.PaymentLinkId || payload.Id || ""}`
      : null,
  }),

  1418: (payload) => ({
    // WhatsApp_Message
    title: "New WhatsApp Message",
    bodyParts: [
      { text: "New WhatsApp message from " },
      {
        text:
          payload.LeadName ||
          payload.FullName ||
          payload.CommunicationRecipient ||
          payload.LeadPhoneNumber ||
          "Unknown",
      },
      { text: `: ${payload.CommunicationMessage || ""}` },
    ],
    icon: FaWhatsapp,
    color: "success",
    action: payload.LeadId
      ? `/leads/opportunities/${payload.LeadId}/communications?subtab=3&logId=${payload.CommunicationLogId || ""}`
      : null,
  }),

  1424: (payload) => ({
    // Bulk_Lead_Assigned
    title: "Bulk Lead Assigned",
    bodyParts: [{ text: "Bulk leads have been assigned to you." }],
    icon: UsersIcon,
    color: "info",
  }),

  // ── Vehicles

  1601: (payload) => ({
    // Vehicle_Registration_Expiry_Reminder
    title: "Vehicle Registration Expiring",
    bodyParts: [
      { text: "Vehicle " },
      {
        text: payload.VehicleName?.trim() || "Unknown Vehicle",
      },
      {
        text: payload.VehicleRegistrationNumber
          ? ` (${payload.VehicleRegistrationNumber})`
          : "",
      },
      { text: " registration is expiring on " },
      {
        text: formatExpiryDate(
          payload.VehicleRegistrationExpiryDate || "Unknown",
        ),
      },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.VehicleId
      ? `/whs/vehicle-register?vehicleId=${payload.VehicleId}`
      : "/whs/vehicle-register",
  }),

  1602: (payload) => ({
    // Vehicle_Insurance_Expiry_Reminder
    title: "Vehicle Insurance Expiring",
    bodyParts: [
      { text: "Vehicle " },
      { text: payload.VehicleName?.trim() || "Unknown Vehicle" },
      {
        text: payload.VehicleRegistrationNumber
          ? ` (${payload.VehicleRegistrationNumber})`
          : "",
      },
      { text: " insurance is expiring on " },
      {
        text: formatExpiryDate(payload.VehicleInsuranceExpiryDate),
      },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.VehicleId
      ? `/whs/vehicle-register?vehicleId=${payload.VehicleId}`
      : "/whs/vehicle-register",
  }),

  1603: (payload) => ({
    // Vehicle_Next_Service_Date_Reminder
    title: "Vehicle Service Due",
    bodyParts: [
      { text: "Vehicle " },
      {
        text: payload.VehicleName?.trim() || "Unknown Vehicle",
      },
      {
        text: payload.VehicleRegistrationNumber
          ? ` (${payload.VehicleRegistrationNumber})`
          : "",
      },
      { text: " is due for service on " },
      {
        text: formatExpiryDate(payload.VehicleNextServiceDate || "Unknown"),
      },
    ],
    icon: WrenchIcon,
    color: "info",
    action: payload.VehicleId
      ? `/whs/vehicle-register?vehicleId=${payload.VehicleId}`
      : "/whs/vehicle-register",
  }),

  1604: (payload) => ({
    // Vehicle_Next_Service_Km_Reminder
    title: "Vehicle Service Odometer Reminder",
    bodyParts: [
      { text: "Vehicle " },
      {
        text: payload.VehicleName?.trim() || "Unknown Vehicle",
      },
      {
        text: payload.VehicleRegistrationNumber
          ? ` (${payload.VehicleRegistrationNumber})`
          : "",
      },

      { text: " is due for service at " },
      {
        text: payload.VehicleNextServiceKilometres || "Unknown",
      },
      { text: " km" },
    ],
    icon: WrenchIcon,
    color: "info",
    action: payload.VehicleId
      ? `/whs/vehicle-register?vehicleId=${payload.VehicleId}`
      : "/whs/vehicle-register",
  }),

  1605: (payload) => ({
    // Vehicle_Roadworthy_Certificate_Expiry_Reminder
    title: "Vehicle Roadworthy Certificate Expiring",
    bodyParts: [
      { text: "Vehicle " },
      { text: payload.VehicleName?.trim() || "Unknown Vehicle" },
      {
        text: payload.VehicleRegistrationNumber
          ? ` (${payload.VehicleRegistrationNumber})`
          : "",
      },
      { text: " roadworthy certificate is expiring on " },
      {
        text: formatExpiryDate(payload.VehicleRoadworthyExpiryDate),
      },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.VehicleId
      ? `/whs/vehicle-register?vehicleId=${payload.VehicleId}`
      : "/whs/vehicle-register",
  }),

  1606: (payload) => ({
    // Vehicle_Maintenance_Reminder
    title: "Vehicle Maintenance Due",
    bodyParts: [
      { text: "Vehicle " },
      { text: payload.VehicleName?.trim() || "Unknown Vehicle" },
      {
        text: payload.VehicleRegistrationNumber
          ? ` (${payload.VehicleRegistrationNumber})`
          : "",
      },
      { text: " is due for maintenance on " },
      {
        text: formatExpiryDate(payload.VehicleMaintenanceDate),
      },
    ],
    icon: WrenchIcon,
    color: "info",
    action: payload.VehicleId
      ? `/whs/vehicle-register?vehicleId=${payload.VehicleId}`
      : "/whs/vehicle-register",
  }),

  // ── Jobs (1700s) ──────────────────────────────────────────────────────────

  1704: (payload) => ({
    // JobTask_Completed
    title: "Job Task Completed",
    bodyParts: [
      { text: "Job Task " },
      { text: payload.TaskName || "Unnamed Task" },
      { text: " has been completed" },
      ...(payload.JobName
        ? [{ text: " for Job " }, { text: payload.JobName }]
        : []),
    ],
    icon: ClipboardDocumentCheckIcon,
    color: "success",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  1701: (payload) => ({
    // JobTask_Assigned
    title: "Job Task Assigned",
    bodyParts: [
      { text: "A Job Task " },
      {
        text: payload.TaskName || "Unnamed Task",
      },
      { text: " has been assigned to you" },
      ...(payload.JobName
        ? [{ text: " for Job " }, { text: payload.JobName || "Unnamed Job" }]
        : []),
    ],
    icon: ClipboardDocumentCheckIcon,
    color: "info",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  1705: (payload) => ({
    // JobTask_AssignedResponsible — FYI for CC recipients (PM/coordinator/fleet manager/admin/owner)
    // who are not themselves the assignee; never reads "assigned to you".
    title: "Job Task Assigned",
    bodyParts: [
      { text: "A Job Task " },
      { text: payload.TaskName || "Unnamed Task" },
      { text: " has been assigned to " },
      { text: payload.AssigneeNames || "a team member" },
      ...(payload.JobName
        ? [{ text: " for Job " }, { text: payload.JobName || "Unnamed Job" }]
        : []),
    ],
    icon: UserGroupIcon,
    color: "info",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  1702: (payload) => ({
    // Task_DueDate_Updated
    title: "Task Due Date Updated",
    bodyParts: [
      { text: "The due date for the task " },
      { text: payload.TaskName || "Unnamed Task" },
      {
        text: ` has been updated to ${formatExpiryDate(payload.TaskDueDate) || "a new date"}`,
      },
      ...(payload.JobName
        ? [{ text: " for job " }, { text: payload.JobName }]
        : []),
    ],
    icon: CalendarIcon,
    color: "info",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  1703: (payload) => ({
    // JobTask_PrecedingTaskDueDateChanged
    title: "Task Due Date Changed",
    bodyParts: [
      { text: "The task due date has changed to " },
      { text: formatExpiryDate(payload.TaskDueDate) || "Unknown Date" },
      { text: " for task " },
      { text: payload.TaskName || "Unnamed Task" },
      ...(payload.PrecedingTaskName
        ? [{ text: ". Preceding task: " }, { text: payload.PrecedingTaskName }]
        : []),
      ...(payload.PrecedingTaskDueDate
        ? [
          {
            text: ` has a new due date of ${formatExpiryDate(
              payload.PrecedingTaskDueDate,
            )}`,
          },
        ]
        : []),
      ...(payload.JobName
        ? [{ text: " in job " }, { text: payload.JobName }]
        : []),
    ],
    icon: CalendarIcon,
    color: "warning",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  1706: (payload) => ({
    // JobTask_LagAdjusted
    title: "Task Schedule Adjusted",
    bodyParts: [
      { text: "The schedule for task " },
      { text: payload.TaskName || "Unnamed Task" },
      {
        text: ` was adjusted — now starts ${formatExpiryDate(
          payload.TaskStartDate,
        )} and is due ${formatExpiryDate(payload.TaskDueDate)}`,
      },
      ...(payload.JobName
        ? [{ text: " for job " }, { text: payload.JobName }]
        : []),
    ],
    icon: CalendarIcon,
    color: "warning",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  1707: (payload) => ({
    // JobTask_AssigneeCompleted — one assignee completed their own part of a multi-assignee
    // task; distinct from 1704 (JobTask_Completed) which is the whole task finishing.
    title: "Assignee Marked Complete",
    bodyParts: [
      { text: payload.NotificationCreatedBy || "A team member" },
      { text: " marked their status as completed on Job Task " },
      { text: payload.TaskName || "Unnamed Task" },
      ...(payload.JobName
        ? [{ text: " for Job " }, { text: payload.JobName }]
        : []),
    ],
    icon: ClipboardDocumentCheckIcon,
    color: "success",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  1710: (payload) => ({
    // Job_Created
    title: "New Job Created",
    bodyParts: [
      { text: "A new job " },
      { text: payload.JobName || "Unnamed Job" },
      { text: " has been added to the system" },
    ],
    icon: DocumentPlusIcon,
    color: "primary",
    action: (payload.HasJobAccess === true || payload.HasJobAccess === "True")
      ? (payload.JobId || payload.jobId ? `/jobs/${payload.JobId || payload.jobId}` : "/jobs")
      : null,
  }),

  1711: (payload) => {
    const formattedDateForURL = payload.FollowUpScheduledDate ? new Date(payload.FollowUpScheduledDate).toLocaleDateString('en-US') : "";
    return {
      // JobActivity_Created
      title: "Job Activity Created",
      bodyParts: [
        { text: "An Activity " },
        { text: payload.FollowUpActivityName || "Unnamed" },
        {
          text: ` has been scheduled on ${formatExpiryDate(payload.FollowUpScheduledDate) || ""} at ${payload.FollowUpScheduledTime || ""}`,
        },
      ],
      icon: CalendarIcon,
      color: "success",
      action: `/jobs/jobs-calendar?date=${formattedDateForURL}&view=month`,
    };
  },

  1712: (payload) => ({
    // JobActivity_Updated
    title: "Job Activity Updated",
    bodyParts: [
      { text: "Your Activity " },
      { text: payload.FollowUpActivityName || "Unnamed" },
      { text: " has been updated" },
      ...(payload.NotificationCreatedBy &&
        payload.LoggedinUserFullName !== payload.NotificationCreatedBy
        ? [{ text: ` by ${payload.NotificationCreatedBy}` }]
        : []),
    ],
    icon: CalendarIcon,
    color: "info",
    action: `/jobs/jobs-calendar?date=${payload.FollowUpScheduledDate || ""}`,
  }),

  1713: (payload) => ({
    // JobClientNote_Added
    title: "Client Note Added",
    bodyParts: [
      { text: "A client note" },
      ...(payload.NoteType ? [{ text: ` (${payload.NoteType})` }] : []),
      { text: " has been added for job " },
      { text: payload.JobName || "Unnamed Job" },
      { text: " and assigned to you" },
    ],
    icon: DocumentPlusIcon,
    color: "info",
    action: payload.JobId ? `/jobs/${payload.JobId}` : null,
  }),

  1801: (payload) => ({
    // Task_Mention
    title: "Mentioned in Task",
    bodyParts: [
      { text: payload.NotificationCreatedBy || "Someone" },
      { text: " mentioned you in a task " },
      {
        text: payload.TaskName || "Unnamed Task",
      },
      ...(payload.JobName
        ? [{ text: " of job " }, { text: payload.JobName }]
        : []),
    ],
    icon: ClipboardDocumentCheckIcon,
    color: "info",
    action: payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId ? `/jobs/schedules/list?jobId=${payload.JobId || payload.jobId || ""}&taskId=${payload.JobTaskId || payload.TaskId || payload.jobTaskId || payload.taskId}&jobName=${encodeURIComponent(payload.JobName || payload.jobName || "")}` : "/jobs/schedules/list",
  }),

  // ── Fallback ──────────────────────────────────────────────────────────────

  DEFAULT: (payload) => ({
    title: payload.title || "New Notification",
    bodyParts: [
      {
        text:
          payload.body ||
          payload.Message ||
          "You have a new system notification.",
      },
    ],
    icon: BellIcon,
    color: "info",
  }),

  // ── Certification Card Expiry ─────────────────────────────────────────────

  1901: (payload) => ({
    // WhiteCard_Expiry_Reminder
    title: "White Card Expiring",
    bodyParts: [
      {
        text: payload.UserName || "A worker",
      },
      { text: "'s White Card is expiring on " },
      {
        text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate),
      },
      {
        text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "",
      },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.UserId
      ? `/whs/contractors-list?userId=${payload.UserId}&tab=documents`
      : "/whs/contractors-list",
  }),

  1902: (payload) => ({
    // SilicaCard_Expiry_Reminder
    title: "Silica Card Expiring",
    bodyParts: [
      {
        text: payload.UserName || "A worker",
      },
      { text: "'s Silica Card is expiring on " },
      {
        text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate),
      },
      {
        text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "",
      },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.UserId
      ? `/whs/contractors-list?userId=${payload.UserId}&tab=documents`
      : "/whs/contractors-list",
  }),

  1903: (payload) => ({
    // AsbestosCard_Expiry_Reminder
    title: "Asbestos Card Expiring",
    bodyParts: [
      {
        text: payload.UserName || "A worker",
      },
      { text: "'s Asbestos Card is expiring on " },
      {
        text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate),
      },
      {
        text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "",
      },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.UserId
      ? `/whs/contractors-list?userId=${payload.UserId}&tab=documents`
      : "/whs/contractors-list",
  }),

  1905: (payload) => ({
    // Contractor_WhiteCard_MissingOnCheckIn — fired at QR check-in when the contractor has
    // no White Card on file; check-in is still allowed, this is just a heads-up for site/
    // compliance staff configured for this job.
    title: "White Card Not Provided",
    bodyParts: [
      { text: payload.UserName || "A contractor" },
      ...(payload.CompanyName ? [{ text: ` of ${payload.CompanyName}` }] : []),
      { text: " has not submitted White Card details" },
      ...(payload.JobName ? [{ text: " and checked into job " }, { text: payload.JobName }] : []),
      { text: "." },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.UserId
      ? `/whs/contractors-list?userId=${payload.UserId}&tab=documents`
      : "/whs/contractors-list",
  }),

  1904: (payload) => ({
    // CompanyDocument_Expiry_Reminder
    title: `${payload.TypeName || "Company Document"} Expiring`,
    bodyParts: [
      {
        text: payload.CompanyName || "A contractor company",
      },
      { text: `'s ${payload.TypeName || "compliance document"} is expiring on ` },
      {
        text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate),
      },
      {
        text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "",
      },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: payload.CompanyId
      ? `/whs/contractors-list/${payload.CompanyId}`
      : "/whs/contractors-list",
  }),

  // ── Internal-User Profile Expiry (Settings > Internal Users) ──────────────

  1906: (payload) => ({
    // AccessCard_Expiry_Reminder
    title: "Access Card Expiring",
    bodyParts: [
      { text: payload.UserName || "An internal user" },
      { text: "'s Access Card is expiring on " },
      { text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate) },
      { text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "" },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: "/settings/user-management",
  }),

  1907: (payload) => ({
    // VisaExpiry_Reminder
    title: "Visa Expiring",
    bodyParts: [
      { text: payload.UserName || "An internal user" },
      { text: "'s Visa is expiring on " },
      { text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate) },
      { text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "" },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: "/settings/user-management",
  }),

  1909: (payload) => ({
    // UserProfileWhiteCard_Expiry_Reminder
    title: "White Card Expiring",
    bodyParts: [
      { text: payload.UserName || "An internal user" },
      { text: "'s White Card is expiring on " },
      { text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate) },
      { text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "" },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: "/settings/user-management",
  }),

  1910: (payload) => ({
    // UserProfileSilicaCard_Expiry_Reminder
    title: "Silica Card Expiring",
    bodyParts: [
      { text: payload.UserName || "An internal user" },
      { text: "'s Silica Card is expiring on " },
      { text: formatExpiryDate(payload.ExpiryDate || payload.expiryDate) },
      { text: payload.DaysLeft != null ? ` (${payload.DaysLeft} day(s) left)` : "" },
    ],
    icon: ExclamationTriangleIcon,
    color: "warning",
    action: "/settings/user-management",
  }),
};

/**
 * Render a notification from an event type code and payload.
 * Returns { title, bodyParts, icon, color }
 */
export const renderNotification = (eventTypeCode, payload) => {
  const templateFn =
    NotificationTemplates[String(eventTypeCode)] ||
    NotificationTemplates["DEFAULT"];
  return templateFn(payload || {});
};
